import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatDateTime } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip, statusChip } from "@/components/Chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Clock, LogIn, LogOut, Calendar, Plane, CheckCircle2, XCircle, AlertCircle,
  Plus, TrendingUp, Target as GoalIcon, Star, Users, MessageCircle,
  Shield, Trash2, KeyRound, BookmarkPlus, Bookmark
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// KPI defined at module scope to prevent input focus loss on re-render
const KPI = ({ label, value, icon: Icon, tone = "aqua" }) => (
  <div className="kpi-card">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${tone === "aqua" ? "bg-brand-aqua-light text-brand-aqua-hover" : "bg-slate-100 text-slate-600"}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <div className="font-heading font-bold text-3xl text-slate-900 num">{value}</div>
  </div>
);

const LEAVE_TYPES = [
  { value: "annual", label: "Annual leave" },
  { value: "sick", label: "Sick leave" },
  { value: "casual", label: "Casual leave" },
  { value: "maternity", label: "Maternity" },
  { value: "paternity", label: "Paternity" },
  { value: "unpaid", label: "Unpaid" },
];

export default function HR() {
  const { user: me } = useAuth();
  const isManagerOrAdmin = me?.role === "admin" || me?.role === "manager";

  const [summary, setSummary] = useState({});
  const [attendance, setAttendance] = useState([]);
  const [attendanceStatus, setAttendanceStatus] = useState({});
  const [leaves, setLeaves] = useState([]);
  const [goals, setGoals] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [presets, setPresets] = useState([]);

  // Dialogs
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ type: "annual", start_date: "", end_date: "", reason: "" });
  const [goalOpen, setGoalOpen] = useState(false);
  const [goalForm, setGoalForm] = useState({ user_id: "", title: "", description: "", target_date: "", kpi: "", progress: 0, status: "in_progress" });
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    user_id: "", period: `${new Date().getFullYear()}-Q${Math.ceil((new Date().getMonth() + 1) / 3)}`,
    overall_rating: 3, strengths: "", development_areas: "", comments: "", status: "draft",
  });
  const [roleOpen, setRoleOpen] = useState(false);
  const [roleForm, setRoleForm] = useState({ role: "", label: "", description: "", rank: 1, copy_from: "staff", preset_id: "" });
  const [presetOpen, setPresetOpen] = useState(false);
  const [presetForm, setPresetForm] = useState({ name: "", description: "", source_role: "staff" });

  const load = useCallback(() => {
    api.get("/hr/summary").then(r => setSummary(r.data));
    api.get("/hr/attendance").then(r => setAttendance(r.data));
    api.get("/hr/attendance/status").then(r => setAttendanceStatus(r.data));
    api.get("/hr/leave").then(r => setLeaves(r.data));
    api.get("/hr/goals").then(r => setGoals(r.data));
    api.get("/hr/reviews").then(r => setReviews(r.data));
    api.get("/users").then(r => setUsers(r.data));
    api.get("/permissions/roles").then(r => setRoles(r.data)).catch(() => {});
    api.get("/permissions/presets").then(r => setPresets(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // ===== Attendance =====
  const clockIn = async () => {
    try { await api.post("/hr/attendance/clock-in", { note: "" }); toast.success("Clocked in"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const clockOut = async () => {
    try { const r = await api.post("/hr/attendance/clock-out", {}); toast.success(`Clocked out — ${r.data.hours}h logged`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // ===== Leave =====
  const submitLeave = async () => {
    if (!leaveForm.start_date || !leaveForm.end_date) return toast.error("Start and end dates required");
    try {
      await api.post("/hr/leave", leaveForm);
      toast.success("Leave requested");
      setLeaveOpen(false);
      setLeaveForm({ type: "annual", start_date: "", end_date: "", reason: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const decideLeave = async (id, decision) => {
    try { await api.patch(`/hr/leave/${id}`, { decision }); toast.success(`Leave ${decision}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // ===== Goals =====
  const createGoal = async () => {
    if (!goalForm.title) return toast.error("Title required");
    const uid = goalForm.user_id || me.id;
    try {
      await api.post("/hr/goals", { ...goalForm, user_id: uid, progress: Number(goalForm.progress) });
      toast.success("Goal created");
      setGoalOpen(false);
      setGoalForm({ user_id: "", title: "", description: "", target_date: "", kpi: "", progress: 0, status: "in_progress" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const updateGoalProgress = async (g, progress) => {
    const p = Math.max(0, Math.min(100, Number(progress)));
    await api.patch(`/hr/goals/${g.id}`, { progress: p, status: p >= 100 ? "achieved" : "in_progress" });
    load();
  };

  // ===== Reviews =====
  const createReview = async () => {
    if (!reviewForm.user_id) return toast.error("Select employee");
    try {
      await api.post("/hr/reviews", { ...reviewForm, overall_rating: Number(reviewForm.overall_rating) });
      toast.success("Review created");
      setReviewOpen(false);
      setReviewForm({ ...reviewForm, user_id: "", strengths: "", development_areas: "", comments: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const acknowledgeReview = async (r) => {
    await api.patch(`/hr/reviews/${r.id}`, { status: "acknowledged" });
    toast.success("Acknowledged");
    load();
  };
  const shareReview = async (r) => {
    await api.patch(`/hr/reviews/${r.id}`, { status: "shared" });
    toast.success("Shared with employee");
    load();
  };

  // ===== Roles =====
  const isAdmin = me?.role === "admin";
  const reviewableUsers = useMemo(
    () => users.filter(u => u.id !== me?.id),
    [users, me?.id]
  );
  const createRole = async () => {
    const name = roleForm.role.trim();
    if (!name) return toast.error("Role name is required");
    try {
      const body = {
        role: name,
        label: roleForm.label || name,
        description: roleForm.description,
        rank: Number(roleForm.rank),
      };
      if (roleForm.preset_id) body.preset_id = roleForm.preset_id;
      else body.copy_from = roleForm.copy_from || null;
      await api.post("/permissions/roles", body);
      toast.success(`Role "${name}" created`);
      setRoleOpen(false);
      setRoleForm({ role: "", label: "", description: "", rank: 1, copy_from: "staff", preset_id: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create role");
    }
  };
  const deleteRole = async (role) => {
    if (!window.confirm(`Delete role "${role}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/permissions/roles/${role}`);
      toast.success(`Role "${role}" deleted`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete role");
    }
  };

  // ===== Presets =====
  const saveAsPreset = (role) => {
    setPresetForm({ name: `${role.label} preset`, description: "", source_role: role.role });
    setPresetOpen(true);
  };
  const createPreset = async () => {
    if (!presetForm.name.trim()) return toast.error("Preset name is required");
    try {
      await api.post("/permissions/presets", presetForm);
      toast.success(`Preset "${presetForm.name}" saved`);
      setPresetOpen(false);
      setPresetForm({ name: "", description: "", source_role: "staff" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save preset");
    }
  };
  const deletePreset = async (p) => {
    if (!window.confirm(`Delete preset "${p.name}"?`)) return;
    try {
      await api.delete(`/permissions/presets/${p.id}`);
      toast.success("Preset deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete preset");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Human Resources" subtitle="Attendance, leave, goals, reviews, and HRIS" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Clocked in now" value={summary.clocked_in_now ?? 0} icon={Clock} />
        <KPI label="Pending leaves" value={summary.pending_leaves ?? 0} icon={Plane} />
        <KPI label="Goals in progress" value={summary.goals_in_progress ?? 0} icon={GoalIcon} />
        <KPI label="Reviews this year" value={summary.reviews_this_year ?? 0} icon={Star} />
      </div>

      <Tabs defaultValue="attendance">
        <TabsList>
          <TabsTrigger value="attendance" data-testid="tab-attendance"><Clock className="w-4 h-4 mr-1.5" />Attendance</TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave"><Plane className="w-4 h-4 mr-1.5" />Leave</TabsTrigger>
          <TabsTrigger value="goals" data-testid="tab-goals"><GoalIcon className="w-4 h-4 mr-1.5" />Goals</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews"><Star className="w-4 h-4 mr-1.5" />Reviews</TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="roles" data-testid="tab-roles"><Shield className="w-4 h-4 mr-1.5" />Roles</TabsTrigger>
          )}
        </TabsList>

        {/* ========== ATTENDANCE ========== */}
        <TabsContent value="attendance" className="mt-4 space-y-4">
          <div className="kpi-card flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-md flex items-center justify-center ${attendanceStatus.clocked_in ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Your status today</div>
                <div className="font-heading font-semibold text-lg text-slate-900">
                  {attendanceStatus.clocked_in ? `Clocked in at ${new Date(attendanceStatus.record?.clock_in).toLocaleTimeString()}` : "Not clocked in yet"}
                </div>
              </div>
            </div>
            {attendanceStatus.clocked_in ? (
              <Button onClick={clockOut} variant="destructive" data-testid="clock-out-btn">
                <LogOut className="w-4 h-4 mr-1.5" /> Clock out
              </Button>
            ) : (
              <Button onClick={clockIn} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="clock-in-btn">
                <LogIn className="w-4 h-4 mr-1.5" /> Clock in
              </Button>
            )}
          </div>

          <div className="table-wrap">
            <table className="w-full text-sm">
              <thead className="table-head">
                <tr>
                  <th className="text-left px-4 py-3">Employee</th>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">In</th>
                  <th className="text-left px-4 py-3">Out</th>
                  <th className="text-right px-4 py-3">Hours</th>
                  <th className="text-left px-4 py-3">Note</th>
                </tr>
              </thead>
              <tbody>
                {attendance.map(a => (
                  <tr key={a.id} className="row">
                    <td className="px-4 py-3 text-slate-800">{a.user_name}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 num">{a.date}</td>
                    <td className="px-4 py-3 text-xs text-slate-600 num">{a.clock_in ? new Date(a.clock_in).toLocaleTimeString() : "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-600 num">{a.clock_out ? new Date(a.clock_out).toLocaleTimeString() : <Chip variant="warning">active</Chip>}</td>
                    <td className="px-4 py-3 text-right font-heading font-semibold num">{a.hours ?? "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 truncate max-w-[240px]">{a.note}</td>
                  </tr>
                ))}
                {attendance.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-slate-500 text-sm">No attendance records yet — clock in to start.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ========== LEAVE ========== */}
        <TabsContent value="leave" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
              <DialogTrigger asChild>
                <Button className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="request-leave-btn">
                  <Plus className="w-4 h-4 mr-1" /> Request leave
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Request time off</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Type</Label>
                    <Select value={leaveForm.type} onValueChange={v => setLeaveForm({...leaveForm, type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{LEAVE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Start date</Label><Input type="date" value={leaveForm.start_date} onChange={e => setLeaveForm({...leaveForm, start_date: e.target.value})} data-testid="leave-start-input" /></div>
                    <div><Label>End date</Label><Input type="date" value={leaveForm.end_date} onChange={e => setLeaveForm({...leaveForm, end_date: e.target.value})} /></div>
                  </div>
                  <div><Label>Reason (optional)</Label><Textarea rows={3} value={leaveForm.reason} onChange={e => setLeaveForm({...leaveForm, reason: e.target.value})} /></div>
                </div>
                <DialogFooter><Button onClick={submitLeave} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="submit-leave-btn">Submit request</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="table-wrap">
            <table className="w-full text-sm">
              <thead className="table-head">
                <tr>
                  <th className="text-left px-4 py-3">Employee</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">Period</th>
                  <th className="text-right px-4 py-3">Days</th>
                  <th className="text-left px-4 py-3">Reason</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leaves.map(l => (
                  <tr key={l.id} className="row" data-testid={`leave-row-${l.id}`}>
                    <td className="px-4 py-3 text-slate-800">{l.user_name}</td>
                    <td className="px-4 py-3 text-slate-600 capitalize">{l.type}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 num">{l.start_date} → {l.end_date}</td>
                    <td className="px-4 py-3 text-right num">{l.days}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 truncate max-w-[200px]">{l.reason || "—"}</td>
                    <td className="px-4 py-3">
                      <Chip variant={l.status === "approved" ? "success" : l.status === "rejected" ? "danger" : "warning"}>{l.status}</Chip>
                    </td>
                    <td className="px-4 py-3 text-right space-x-1">
                      {isManagerOrAdmin && l.status === "pending" && l.user_id !== me.id && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => decideLeave(l.id, "approved")} className="h-7 px-2 text-xs text-emerald-600 border-emerald-200" data-testid={`approve-leave-${l.id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => decideLeave(l.id, "rejected")} className="h-7 px-2 text-xs text-red-600 border-red-200" data-testid={`reject-leave-${l.id}`}>
                            <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {leaves.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-slate-500 text-sm">No leave requests.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ========== GOALS ========== */}
        <TabsContent value="goals" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Dialog open={goalOpen} onOpenChange={setGoalOpen}>
              <DialogTrigger asChild>
                <Button className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="add-goal-btn">
                  <Plus className="w-4 h-4 mr-1" /> New Goal
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Set a goal</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  {isManagerOrAdmin && (
                    <div><Label>For</Label>
                      <Select value={goalForm.user_id || me?.id} onValueChange={v => setGoalForm({...goalForm, user_id: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                      </Select></div>
                  )}
                  <div><Label>Title</Label><Input value={goalForm.title} onChange={e => setGoalForm({...goalForm, title: e.target.value})} data-testid="goal-title-input" /></div>
                  <div><Label>KPI / measurement</Label><Input value={goalForm.kpi} onChange={e => setGoalForm({...goalForm, kpi: e.target.value})} placeholder="e.g. Sign 5 new FigureCX customers" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Target date</Label><Input type="date" value={goalForm.target_date} onChange={e => setGoalForm({...goalForm, target_date: e.target.value})} /></div>
                    <div><Label>Progress %</Label><Input type="number" min="0" max="100" value={goalForm.progress} onChange={e => setGoalForm({...goalForm, progress: e.target.value})} /></div>
                  </div>
                  <div><Label>Description</Label><Textarea rows={3} value={goalForm.description} onChange={e => setGoalForm({...goalForm, description: e.target.value})} /></div>
                </div>
                <DialogFooter><Button onClick={createGoal} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="save-goal-btn">Save goal</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {goals.map(g => {
              const userName = users.find(u => u.id === g.user_id)?.name || "—";
              return (
                <div key={g.id} className="kpi-card" data-testid={`goal-card-${g.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-slate-800">{g.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{userName}{g.target_date && ` · target ${g.target_date}`}</div>
                    </div>
                    <Chip variant={g.status === "achieved" ? "success" : g.status === "missed" ? "danger" : "info"}>{g.status.replace("_", " ")}</Chip>
                  </div>
                  {g.kpi && <div className="text-xs text-slate-600 mt-2 italic">"{g.kpi}"</div>}
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-slate-500 mb-1"><span>Progress</span><span className="num">{g.progress}%</span></div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${g.progress >= 100 ? "bg-emerald-500" : "bg-brand-aqua"}`} style={{ width: `${g.progress}%` }} />
                    </div>
                    <Input type="range" min="0" max="100" value={g.progress} onChange={e => updateGoalProgress(g, e.target.value)} className="mt-2 h-1" />
                  </div>
                </div>
              );
            })}
            {goals.length === 0 && <div className="col-span-full text-center py-12 text-slate-500 text-sm">No goals set yet.</div>}
          </div>
        </TabsContent>

        {/* ========== REVIEWS ========== */}
        <TabsContent value="reviews" className="mt-4 space-y-4">
          {isManagerOrAdmin && (
            <div className="flex justify-end">
              <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="add-review-btn">
                    <Plus className="w-4 h-4 mr-1" /> Start Review
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader><DialogTitle>Performance review</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Employee</Label>
                      <Select value={reviewForm.user_id} onValueChange={v => setReviewForm({...reviewForm, user_id: v})}>
                        <SelectTrigger data-testid="review-user-select"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{reviewableUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Period</Label><Input value={reviewForm.period} onChange={e => setReviewForm({...reviewForm, period: e.target.value})} placeholder="2026-Q1" /></div>
                      <div><Label>Overall rating (1-5)</Label>
                        <Select value={String(reviewForm.overall_rating)} onValueChange={v => setReviewForm({...reviewForm, overall_rating: Number(v)})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n} — {["Poor","Needs improvement","Meets","Exceeds","Outstanding"][n-1]}</SelectItem>)}
                          </SelectContent>
                        </Select></div>
                    </div>
                    <div><Label>Strengths</Label><Textarea rows={2} value={reviewForm.strengths} onChange={e => setReviewForm({...reviewForm, strengths: e.target.value})} /></div>
                    <div><Label>Development areas</Label><Textarea rows={2} value={reviewForm.development_areas} onChange={e => setReviewForm({...reviewForm, development_areas: e.target.value})} /></div>
                    <div><Label>Comments</Label><Textarea rows={2} value={reviewForm.comments} onChange={e => setReviewForm({...reviewForm, comments: e.target.value})} /></div>
                    <div><Label>Status</Label>
                      <Select value={reviewForm.status} onValueChange={v => setReviewForm({...reviewForm, status: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft (private)</SelectItem>
                          <SelectItem value="shared">Share with employee</SelectItem>
                        </SelectContent>
                      </Select></div>
                  </div>
                  <DialogFooter><Button onClick={createReview} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="save-review-btn">Save review</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {reviews.map(r => {
              const employee = users.find(u => u.id === r.user_id)?.name || "—";
              const isMine = r.user_id === me?.id;
              return (
                <div key={r.id} className="kpi-card" data-testid={`review-card-${r.id}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-heading font-semibold text-slate-900">{employee}</div>
                      <div className="text-xs text-slate-500">{r.period} · reviewed by {r.reviewer_name}</div>
                    </div>
                    <Chip variant={r.status === "acknowledged" ? "success" : r.status === "shared" ? "info" : "default"}>{r.status}</Chip>
                  </div>
                  <div className="flex items-center gap-1 mb-3">
                    {[1,2,3,4,5].map(n => (
                      <Star key={n} className={`w-5 h-5 ${n <= r.overall_rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
                    ))}
                    <span className="ml-2 text-sm font-medium text-slate-700">{r.overall_rating}/5</span>
                  </div>
                  {r.strengths && <div className="text-xs mb-2"><span className="uppercase tracking-wider text-slate-500 font-medium">Strengths: </span><span className="text-slate-700">{r.strengths}</span></div>}
                  {r.development_areas && <div className="text-xs mb-2"><span className="uppercase tracking-wider text-slate-500 font-medium">Development: </span><span className="text-slate-700">{r.development_areas}</span></div>}
                  {r.comments && <div className="text-xs text-slate-600 italic mt-2">"{r.comments}"</div>}
                  <div className="mt-3 flex gap-2">
                    {isMine && r.status === "shared" && (
                      <Button size="sm" onClick={() => acknowledgeReview(r)} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white text-xs h-7" data-testid={`ack-review-${r.id}`}>Acknowledge</Button>
                    )}
                    {isManagerOrAdmin && r.status === "draft" && (
                      <Button size="sm" variant="outline" onClick={() => shareReview(r)} className="text-xs h-7">Share with employee</Button>
                    )}
                  </div>
                </div>
              );
            })}
            {reviews.length === 0 && <div className="col-span-full text-center py-12 text-slate-500 text-sm">No reviews yet.</div>}
          </div>
        </TabsContent>

        {/* ========== ROLES (admin only) ========== */}
        {isAdmin && (
          <TabsContent value="roles" className="mt-4 space-y-4">
            <div className="text-xs bg-blue-50 text-blue-800 border border-blue-200 px-3 py-2 rounded-md dark:bg-blue-950/40 dark:text-blue-200 dark:border-blue-900">
              Create custom job roles for your organisation. New roles start with permissions copied from an existing role — fine-tune them from <span className="font-semibold">Admin › Roles &amp; Permissions</span>. Seeded system roles cannot be deleted.
            </div>

            <div className="flex justify-end">
              <Dialog open={roleOpen} onOpenChange={setRoleOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="create-role-btn">
                    <Plus className="w-4 h-4 mr-1" /> Create Role
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Create a new role</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Role key (identifier)</Label>
                      <Input
                        placeholder="e.g. finance_lead"
                        value={roleForm.role}
                        onChange={e => setRoleForm({ ...roleForm, role: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                        data-testid="role-key-input"
                      />
                      <div className="text-[11px] text-slate-500 mt-1">Lowercase letters, numbers, underscores. Used internally.</div>
                    </div>
                    <div>
                      <Label>Display label</Label>
                      <Input placeholder="e.g. Finance Lead" value={roleForm.label} onChange={e => setRoleForm({ ...roleForm, label: e.target.value })} data-testid="role-label-input" />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea rows={2} placeholder="What does this role do?" value={roleForm.description} onChange={e => setRoleForm({ ...roleForm, description: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Level</Label>
                        <Select value={String(roleForm.rank)} onValueChange={v => setRoleForm({ ...roleForm, rank: Number(v) })}>
                          <SelectTrigger data-testid="role-rank-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Staff level</SelectItem>
                            <SelectItem value="2">Manager level</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Copy permissions from</Label>
                        <Select
                          value={roleForm.preset_id ? `preset:${roleForm.preset_id}` : `role:${roleForm.copy_from}`}
                          onValueChange={v => {
                            if (v.startsWith("preset:")) setRoleForm({ ...roleForm, preset_id: v.slice(7), copy_from: "" });
                            else setRoleForm({ ...roleForm, copy_from: v.slice(5), preset_id: "" });
                          }}
                        >
                          <SelectTrigger data-testid="role-copyfrom-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {roles.map(r => <SelectItem key={r.role} value={`role:${r.role}`}>Role · {r.label}</SelectItem>)}
                            {presets.length > 0 && presets.map(p => (
                              <SelectItem key={p.id} value={`preset:${p.id}`}>Preset · {p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={createRole} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="save-role-btn">Create role</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="table-wrap">
              <table className="w-full text-sm">
                <thead className="table-head">
                  <tr>
                    <th className="text-left px-4 py-3">Role</th>
                    <th className="text-left px-4 py-3">Description</th>
                    <th className="text-left px-4 py-3">Level</th>
                    <th className="text-right px-4 py-3">Permissions</th>
                    <th className="text-left px-4 py-3">Type</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map(r => (
                    <tr key={r.role} className="row" data-testid={`role-row-${r.role}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <KeyRound className="w-3.5 h-3.5 text-slate-400" />
                          <div>
                            <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{r.label}</div>
                            <div className="text-[11px] text-slate-500 font-mono">{r.role}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 max-w-[280px]">{r.description || "—"}</td>
                      <td className="px-4 py-3">
                        <Chip variant={r.rank >= 3 ? "danger" : r.rank === 2 ? "aqua" : "info"}>
                          {r.rank >= 3 ? "Admin" : r.rank === 2 ? "Manager" : "Staff"}
                        </Chip>
                      </td>
                      <td className="px-4 py-3 text-right font-heading font-semibold num">{r.permissions_count}</td>
                      <td className="px-4 py-3">
                        <Chip variant={r.is_custom ? "purple" : "default"}>{r.is_custom ? "Custom" : "System"}</Chip>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button size="sm" variant="outline" onClick={() => saveAsPreset(r)}
                                  className="h-7 px-2 text-xs"
                                  data-testid={`save-preset-${r.role}`}>
                            <BookmarkPlus className="w-3.5 h-3.5 mr-1" /> Save as preset
                          </Button>
                          {r.is_custom ? (
                            <Button size="sm" variant="outline" onClick={() => deleteRole(r.role)}
                                    className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50"
                                    data-testid={`delete-role-${r.role}`}>
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                            </Button>
                          ) : (
                            <span className="text-[11px] text-slate-400 pl-1">system</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {roles.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-slate-500 text-sm">No roles defined.</td></tr>}
                </tbody>
              </table>
            </div>

            {/* Role Presets */}
            <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-heading font-semibold text-lg text-slate-900 dark:text-slate-100 flex items-center gap-2">
                    <Bookmark className="w-4 h-4 text-brand-aqua-hover" /> Role Presets
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">Save permission templates and reuse them when you create new roles.</p>
                </div>
              </div>

              {presets.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-sm bg-slate-50 dark:bg-slate-900/40 rounded-md border border-dashed border-slate-200 dark:border-slate-800">
                  No presets yet. Click <strong>Save as preset</strong> on any role above to create your first template.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {presets.map(p => (
                    <div key={p.id} className="kpi-card" data-testid={`preset-card-${p.id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-medium text-slate-800 dark:text-slate-100">{p.name}</div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            from <span className="font-mono">{p.source_role}</span> · <span className="num">{(p.permissions || []).length}</span> permissions
                          </div>
                          {p.description && <div className="text-xs text-slate-500 mt-1 italic">{p.description}</div>}
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => deletePreset(p)}
                                className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"
                                data-testid={`delete-preset-${p.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Save as preset dialog */}
            <Dialog open={presetOpen} onOpenChange={setPresetOpen}>
              <DialogContent>
                <DialogHeader><DialogTitle>Save role as preset</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Preset name</Label>
                    <Input value={presetForm.name}
                           onChange={e => setPresetForm({ ...presetForm, name: e.target.value })}
                           data-testid="preset-name-input" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea rows={2} value={presetForm.description}
                              onChange={e => setPresetForm({ ...presetForm, description: e.target.value })} />
                  </div>
                  <div className="text-xs text-slate-500">
                    Copying permissions from role <span className="font-mono">{presetForm.source_role}</span>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={createPreset} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="save-preset-btn">
                    Save preset
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
